import type { ReactNode } from 'react'
import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { TriangleAlert } from 'lucide-react-native'
import { colors } from '@chillout/core'
import { Button, InlineAlert, CARD_ICON_COMPONENTS } from '@chillout/ui'
import type { CardIconKey } from '@chillout/ui'
import { AdminModal, ModalActions } from '../shared/Modal'
import type { LoyaltyConfig, RewardItem } from './useLoyaltyConfig'
import { useStampTargetImpact } from './useStampTargetImpact'

interface SaveConfirmModalProps {
  visible: boolean
  serverConfig: LoyaltyConfig
  pendingConfig: LoyaltyConfig
  rewardItems: RewardItem[]
  saving: boolean
  error: boolean
  onConfirm: () => void
  onCancel: () => void
}

export function SaveConfirmModal({
  visible,
  serverConfig,
  pendingConfig,
  rewardItems,
  saving,
  error,
  onConfirm,
  onCancel,
}: SaveConfirmModalProps) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language === 'sv' ? 'sv' : 'en'

  const stampTargetReduced = pendingConfig.stampTarget < serverConfig.stampTarget
  const impactQuery = useStampTargetImpact(stampTargetReduced ? pendingConfig.stampTarget : null)
  const impactCount = impactQuery.data ?? '–'

  const changes = computeChanges(serverConfig, pendingConfig, rewardItems, lang, t, impactCount)

  return (
    <AdminModal
      visible={visible}
      onClose={onCancel}
      closeAccessibilityLabel={t('loyalty.saveConfirm.closeAccessibilityLabel')}
      title={t('loyalty.saveConfirm.title')}
      subtitle={t('loyalty.saveConfirm.subtitle')}
      actions={
        <ModalActions>
          <Button
            label={t('common.cancel')}
            variant="secondary"
            className="flex-1"
            onPress={onCancel}
            disabled={saving}
          />
          <Button
            label={t('loyalty.saveConfirm.confirm')}
            loadingLabel={t('loyalty.saveConfirm.saving')}
            variant="primary"
            className="flex-1"
            loading={saving}
            onPress={onConfirm}
          />
        </ModalActions>
      }
    >
      <View className="mt-5 gap-3">
        {changes.map((change) => (
          <ChangeRow key={change.label} {...change} />
        ))}
        {error ? <InlineAlert message={t('loyalty.saveConfirm.saveError')} /> : null}
      </View>
    </AdminModal>
  )
}

interface ChangeRowProps {
  label: string
  content: ReactNode
  warning?: string
  info?: string
}

function ChangeRow({ label, content, warning, info }: ChangeRowProps) {
  return (
    <View className="rounded-lg border border-border bg-canvas-primary px-4 py-3">
      <Text className="text-xs font-semibold uppercase tracking-wide text-secondary">{label}</Text>
      <View className="mt-1">{content}</View>
      {warning ? (
        <View className="mt-2 flex-row items-start gap-2">
          <TriangleAlert size={14} color={colors.warning} strokeWidth={2} className="mt-0.5" />
          <Text className="min-w-0 flex-1 text-xs text-warning">{warning}</Text>
        </View>
      ) : null}
      {info ? (
        <Text className="mt-2 text-xs text-secondary">{info}</Text>
      ) : null}
    </View>
  )
}

function TextDiff({ before, after }: { before: string; after: string }) {
  return (
    <View className="flex-row items-center gap-2">
      <Text className="text-sm text-secondary" numberOfLines={1}>{before}</Text>
      <Text className="text-sm text-muted">&rarr;</Text>
      <Text className="text-sm font-medium text-primary" numberOfLines={1}>{after}</Text>
    </View>
  )
}

function CardIcon({ iconKey }: { iconKey: CardIconKey }) {
  const Icon = CARD_ICON_COMPONENTS[iconKey]
  if (!Icon) return null
  return <Icon size={16} color={colors.text.primary} strokeWidth={2} />
}

function CompletionMilestoneDiff({
  serverReward,
  pendingReward,
  serverIcon,
  pendingIcon,
  rewardChanged,
  iconChanged,
}: {
  serverReward: string
  pendingReward: string
  serverIcon: CardIconKey
  pendingIcon: CardIconKey
  rewardChanged: boolean
  iconChanged: boolean
}) {
  return (
    <View className="gap-2">
      {rewardChanged ? (
        <View className="flex-row items-center gap-2">
          <Text className="text-sm text-secondary" numberOfLines={1}>{serverReward}</Text>
          <Text className="text-sm text-muted">&rarr;</Text>
          <Text className="text-sm font-medium text-primary" numberOfLines={1}>{pendingReward}</Text>
        </View>
      ) : null}
      {iconChanged ? (
        <View className="flex-row items-center gap-2">
          <CardIcon iconKey={serverIcon} />
          <Text className="text-sm text-muted">&rarr;</Text>
          <CardIcon iconKey={pendingIcon} />
        </View>
      ) : null}
    </View>
  )
}

interface Change {
  label: string
  content: ReactNode
  warning?: string
  info?: string
}

type Translate = (key: string, options?: Record<string, unknown>) => string

function computeChanges(
  server: LoyaltyConfig,
  pending: LoyaltyConfig,
  rewardItems: RewardItem[],
  lang: 'en' | 'sv',
  t: Translate,
  impactCount: number | string,
): Change[] {
  const changes: Change[] = []
  const rewardName = (id: string) => {
    const item = rewardItems.find((r) => r.id === id)
    if (!item) return id
    return item.name[lang] || item.name.en
  }
  const earningMethodLabel = (method: string) =>
    method === 'spend_based' ? t('loyalty.earningRule.spendBased') : t('loyalty.earningRule.perMeal')

  if (pending.earningMethod !== server.earningMethod) {
    changes.push({
      label: t('loyalty.earningRule.sectionTitle'),
      content: <TextDiff before={earningMethodLabel(server.earningMethod)} after={earningMethodLabel(pending.earningMethod)} />,
    })
  }

  if (pending.earningMethod === 'spend_based' && pending.amountPerStamp !== server.amountPerStamp) {
    changes.push({
      label: t('loyalty.earningRule.amountPerStamp'),
      content: <TextDiff before={`${server.amountPerStamp} ${t('loyalty.earningRule.currency')}`} after={`${pending.amountPerStamp} ${t('loyalty.earningRule.currency')}`} />,
    })
  }

  if (pending.stampTarget !== server.stampTarget) {
    changes.push({
      label: t('loyalty.cardSetup.stampsPerCard'),
      content: <TextDiff before={String(server.stampTarget)} after={String(pending.stampTarget)} />,
      warning: pending.stampTarget < server.stampTarget
        ? t('loyalty.saveConfirm.stampTargetDecreaseWarning', { count: impactCount })
        : undefined,
    })
  }

  const rewardChanged = pending.completionRewardItemId !== server.completionRewardItemId
  const iconChanged = pending.completionCardIcon !== server.completionCardIcon

  if (rewardChanged || iconChanged) {
    changes.push({
      label: t('loyalty.saveConfirm.completionMilestone'),
      content: (
        <CompletionMilestoneDiff
          serverReward={rewardName(server.completionRewardItemId)}
          pendingReward={rewardName(pending.completionRewardItemId)}
          serverIcon={server.completionCardIcon}
          pendingIcon={pending.completionCardIcon}
          rewardChanged={rewardChanged}
          iconChanged={iconChanged}
        />
      ),
      warning: rewardChanged ? t('loyalty.saveConfirm.completionRewardInfo') : undefined,
    })
  }

  return changes
}